import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Box, Typography } from '@mui/material';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './privacy-policy.scss';

gsap.registerPlugin(ScrollTrigger);

function PrivacyPolicy() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const animateSplitText = (element: HTMLElement) => {
    const text = element.textContent || '';
    element.innerHTML = text
      .split('')
      .map(char => char === ' ' ? ' ' : `<span class="split-char">${char}</span>`)
      .join('');

    const chars = element.querySelectorAll('.split-char');
    gsap.fromTo(chars,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      }
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (heroRef.current) {
        gsap.fromTo(heroRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
        );
      }
      if (titleRef.current) {
        animateSplitText(titleRef.current);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Fabbri Agricoltura</title>
        <meta name="description" content="Informativa sulla privacy di Fabbri Agricoltura ai sensi del Regolamento UE 2016/679 (GDPR)." />
      </Helmet>

      <Box className="legal-page-agricola" id="top">
        <Box className="legal-hero-agricola" ref={heroRef}>
          <Container maxWidth="lg" className="legal-hero-content">
            <Typography variant="h1" className="legal-hero-title split-text" ref={titleRef}>
              Privacy Policy
            </Typography>
            <Typography className="legal-hero-subtitle">
              Informativa sul trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR)
            </Typography>
          </Container>
        </Box>

        <Box className="legal-content-section">
          <Container maxWidth="lg">

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">1.</span> Titolare del trattamento
              </Typography>
              <Typography className="legal-text">
                Il titolare del trattamento dei dati personali è:<br />
                <strong>Fabbri Agricoltura di Fabbri Dr. Francesco</strong><br />
                <a href="https://maps.app.goo.gl/ws26E8mfGKWDVsZL9" target="_blank" rel="noopener noreferrer">Via Per S. Felice, 3 - 41031 Camposanto (MO)</a><br />
                P.IVA: 04004150365<br />
                Email: <a href="mailto:fabbri.agricoltura@outlook.it">fabbri.agricoltura@outlook.it</a><br />
                Telefono: <a href="tel:+393200441982">+39 3200441982</a>
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">2.</span> Tipologie di dati raccolti
              </Typography>
              <Typography className="legal-text">
                Il sito raccoglie le seguenti categorie di dati personali:
              </Typography>
              <ul className="legal-list">
                <li><strong>Dati di navigazione:</strong> indirizzo IP, tipo di browser, sistema operativo, pagine visitate, orari di accesso. Questi dati vengono raccolti automaticamente durante la navigazione</li>
                <li><strong>Cookie:</strong> dati raccolti tramite cookie tecnici e analitici (vedi la nostra <a href="/cookie-policy">Cookie Policy</a>)</li>
              </ul>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">3.</span> Finalità del trattamento
              </Typography>
              <Typography className="legal-text">
                I dati personali sono trattati per le seguenti finalità:
              </Typography>
              <ul className="legal-list">
                <li><strong>Analisi statistica:</strong> tramite Google Analytics 4, in forma anonima e aggregata, per comprendere come gli utenti utilizzano il sito e migliorarne l'esperienza</li>
                <li><strong>Funzionamento del sito:</strong> cookie tecnici necessari al corretto funzionamento del sito</li>
              </ul>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">4.</span> Base giuridica del trattamento
              </Typography>
              <ul className="legal-list">
                <li><strong>Consenso</strong> (art. 6, par. 1, lett. a GDPR): per i cookie analitici</li>
                <li><strong>Legittimo interesse</strong> (art. 6, par. 1, lett. f GDPR): per i cookie tecnici necessari al funzionamento del sito</li>
              </ul>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">5.</span> Modalità di trattamento
              </Typography>
              <Typography className="legal-text">
                I dati personali sono trattati con strumenti elettronici e con misure di sicurezza adeguate a garantirne la riservatezza. Il trattamento avviene nel rispetto dei principi di liceità, correttezza, trasparenza, limitazione delle finalità, minimizzazione dei dati e limitazione della conservazione.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">6.</span> Conservazione dei dati
              </Typography>
              <Typography className="legal-text">
                I dati di navigazione vengono conservati per un massimo di 26 mesi (Google Analytics).
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">7.</span> Comunicazione e diffusione dei dati
              </Typography>
              <Typography className="legal-text">
                I dati personali non vengono venduti né ceduti a terzi. Possono essere comunicati a:
              </Typography>
              <ul className="legal-list">
                <li><strong>Google LLC</strong> — per il servizio Google Analytics 4 (i dati possono essere trasferiti negli USA in conformità alle garanzie previste dal GDPR)</li>
              </ul>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">8.</span> Diritti dell'interessato
              </Typography>
              <Typography className="legal-text">
                Ai sensi degli articoli 15-22 del GDPR, l'utente ha il diritto di:
              </Typography>
              <ul className="legal-list">
                <li>Accedere ai propri dati personali</li>
                <li>Ottenere la rettifica o la cancellazione dei dati</li>
                <li>Limitare il trattamento</li>
                <li>Opporsi al trattamento</li>
                <li>Richiedere la portabilità dei dati</li>
                <li>Revocare il consenso in qualsiasi momento</li>
                <li>Proporre reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</a>)</li>
              </ul>
              <Typography className="legal-text">
                Per esercitare i propri diritti, contattare il titolare all'indirizzo <a href="mailto:fabbri.agricoltura@outlook.it">fabbri.agricoltura@outlook.it</a>.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">9.</span> Servizi di terze parti
              </Typography>
              <ul className="legal-list">
                <li><strong>Google Analytics 4</strong> (Google LLC) — servizio di analisi web. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy Google</a></li>
                <li><strong>Google Tag Manager</strong> (Google LLC) — servizio di gestione tag</li>
                <li><strong>Google Maps</strong> (Google LLC) — servizio di mappe nella pagina Contatti</li>
              </ul>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">10.</span> Modifiche alla presente informativa
              </Typography>
              <Typography className="legal-text">
                Il titolare si riserva il diritto di apportare modifiche alla presente informativa in qualsiasi momento. Si consiglia di consultare periodicamente questa pagina.
              </Typography>
            </Box>

            <Box className="legal-block legal-footer-info">
              <Typography className="legal-text">
                <strong>Fabbri Agricoltura di Fabbri Dr. Francesco</strong><br />
                <a href="https://maps.app.goo.gl/ws26E8mfGKWDVsZL9" target="_blank" rel="noopener noreferrer">Via Per S. Felice, 3 - 41031 Camposanto (MO)</a><br />
                P.IVA 04004150365
              </Typography>
              <Typography className="legal-text legal-update">
                Ultimo aggiornamento: agosto 2026
              </Typography>
            </Box>

          </Container>
        </Box>
      </Box>
    </>
  );
}

export default PrivacyPolicy;
